import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Flex,
  Icon,
  Input,
  ScrollArea,
  Text,
} from '@kognitos/lattice';
import { parseResourceName } from '@/shared/utils';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  useOrganizationSwitcherStore,
} from '@/shared/stores';
import { getRoutePath } from '@/routes/util';
import { useCurrentUserOrganizations } from '@/generated/hooks/agora/v1/organization';
import { useCurrentUserOrganizationWorkspaces } from '@/generated/hooks/agora/v1/workspace';

const OrganizationSwitcher = () => {
  const isOpen = useOrganizationSwitcherStore(state => state.isOpen);
  const openOrgSwitcher = useOrganizationSwitcherStore(state => state.open);
  const closeOrgSwitcher = useOrganizationSwitcherStore(state => state.close);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { t: tOrg } = useTranslation('organization');
  const { t: tCommon } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');
  const navigate = useNavigate();
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );

  const { data: organizationsData } = useCurrentUserOrganizations();

  // Fetch workspaces for the selected org to get the first workspace for navigation
  const {
    data: selectedOrgWorkspaces,
    isLoading: isSelectedOrgWorkspacesLoading,
  } = useCurrentUserOrganizationWorkspaces(selectedOrgId || '', undefined, {
    query: {
      enabled: Boolean(selectedOrgId),
    },
  });

  const userOrgs = useMemo(
    () =>
      organizationsData?.data.user_organizations?.map(org => ({
        ...org,
        name: parseResourceName(org.name!).organization,
      })) || [],
    [organizationsData],
  );

  // Get the current organization object
  const currentOrganization = useMemo(() => {
    return (
      userOrgs.find(org => org.name === currentOrganizationId) || userOrgs[0]
    );
  }, [userOrgs, currentOrganizationId]);

  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) {
      return userOrgs;
    }
    return userOrgs.filter(org =>
      org.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [userOrgs, searchQuery]);

  const handleOrganizationSelect = (orgId: string) => {
    setSelectedOrgId(orgId);
  };

  const handleSwitch = () => {
    if (!selectedOrgId) return;

    // Get the first workspace of the selected organization
    const firstWorkspace = selectedOrgWorkspaces?.data.user_workspaces?.[0];
    const newWorkspaceId = firstWorkspace
      ? parseResourceName(firstWorkspace.name).workspace
      : '';

    // Navigate to Automations screen
    if (!isSelectedOrgWorkspacesLoading && newWorkspaceId) {
      navigate(
        getRoutePath('AUTOMATIONS', {
          orgId: selectedOrgId,
          workspaceId: newWorkspaceId,
        }),
      );
    }

    closeOrgSwitcher();
    setSelectedOrgId(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openOrgSwitcher();
    } else {
      closeOrgSwitcher();
      setSelectedOrgId(null);
      setSearchQuery('');
    }
  };

  const showSearch = userOrgs.length > 4;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="p-6">
        <DialogHeader className="pb-4">
          <DialogTitle>{tOrg('organization.switch.title')}</DialogTitle>
        </DialogHeader>

        <Flex direction="column" gap={4}>
          {showSearch && (
            <Flex className="relative w-full">
              <Icon
                type="Search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
              />
              <Input
                placeholder={tOrg('organization.switch.placeholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </Flex>
          )}

          {/* Current Organization - Fixed */}
          {currentOrganization ? (
            <Flex direction="column" gap={2} className="w-full">
              <Text level="small" color="muted" weight="medium">
                {tOrg('organization.switch.current')}
              </Text>
              {userOrgs
                .filter(org => org.name === currentOrganizationId)
                .map(org => (
                  <Button
                    key={org.name}
                    variant="outline"
                    className="w-full justify-start h-auto p-3"
                    disabled
                  >
                    <Avatar className="h-8 w-8 rounded-lg mr-3">
                      <AvatarImage src={org.logo_data_uri} alt={org.title} />
                      <AvatarFallback className="rounded-lg bg-sidebar-accent">
                        <Icon
                          type="CircleDashed"
                          size="sm"
                          className="text-foreground"
                        />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start">
                      <Text level="small" weight="medium">
                        {org.title}
                      </Text>
                      <Text level="small" color="muted">
                        {tOrg('organization.switch.current')}
                      </Text>
                    </div>
                  </Button>
                ))}
            </Flex>
          ) : (
            <Flex direction="column" gap={2} className="w-full">
              <Text level="small" color="muted">
                {tOrg('organization.switch.noOrganizations')}
              </Text>
            </Flex>
          )}

          {/* Other Organizations - Scrollable */}
          {filteredOrganizations.filter(
            org => org.name !== currentOrganizationId,
          ).length > 0 && (
            <Flex direction="column" gap={2} width="full">
              <Text level="small" color="muted" weight="medium">
                {tNav('navigation.organizationSwitcher.otherOrganizations')}
              </Text>
              <ScrollArea className="h-48 w-full">
                <Flex direction="column" gap={1} width="full">
                  {filteredOrganizations
                    .filter(org => org.name !== currentOrganizationId)
                    .map(org => (
                      <Button
                        key={org.name}
                        variant={
                          selectedOrgId === org.name ? 'secondary' : 'ghost'
                        }
                        className="w-full justify-start h-auto p-3"
                        onClick={() => handleOrganizationSelect(org.name!)}
                      >
                        <Avatar className="h-8 w-8 rounded-lg mr-3">
                          <AvatarImage
                            src={org.logo_data_uri}
                            alt={org.title}
                          />
                          <AvatarFallback className="rounded-lg bg-sidebar-accent">
                            <Icon
                              type="CircleDashed"
                              size="sm"
                              className="text-foreground"
                            />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col items-start">
                          <Text level="small" weight="medium">
                            {org.title}
                          </Text>
                        </div>
                      </Button>
                    ))}
                </Flex>
              </ScrollArea>
            </Flex>
          )}

          {filteredOrganizations.filter(
            org => org.name !== currentOrganizationId,
          ).length === 0 &&
            searchQuery && (
              <Flex justify="center" className="py-4" width="full">
                <Text level="small" color="muted">
                  {tOrg('organization.switch.noMatches', {
                    query: searchQuery,
                  })}
                </Text>
              </Flex>
            )}

          <Flex justify="end" gap={2} width="full">
            <Button variant="outline" onClick={closeOrgSwitcher}>
              {tCommon('common.action.cancel')}
            </Button>
            <Button
              onClick={handleSwitch}
              disabled={
                !selectedOrgId || selectedOrgId === currentOrganizationId
              }
            >
              {tCommon('common.action.switch')}
            </Button>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationSwitcher;
